import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductGallery from "@/components/ProductGallery";
import SizeGuide from "@/components/SizeGuide";

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setSize(r.data.sizes?.[0] || null);
    });
  }, [id]);

  if (!product) return <div className="h-screen bg-[#0A0A0C]" />;

  const add = () => {
    if (!size) return toast.error("Please select a size");
    addItem(product, size, 1);
    toast.success(`${product.name} added to bag`);
  };

  return (
    <div className="pt-[72px]">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-10 md:py-16">
        <Link to="/shop" className="overline link-underline inline-flex items-center gap-2 mb-10">
          <ArrowLeft size={14} /> Back to Collection
        </Link>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <motion.div
            className="md:col-span-7"
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
          >
            <ProductGallery images={product.images && product.images.length ? product.images : [product.image]} alt={product.name} />
          </motion.div>

          <div className="md:col-span-5 md:sticky md:top-28 md:self-start">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}>
              <p className="overline mb-3">{product.category}</p>
              <h1 data-testid="product-title" className="font-serif text-5xl md:text-7xl text-bone leading-[0.9]">{product.name}</h1>
              <p className="font-mono text-2xl text-bone mt-6">{formatINR(product.price)}</p>
              <div className="w-full h-px bg-line my-8" />
              <p className="text-ash leading-relaxed">{product.description}</p>
              <p className="font-mono text-xs uppercase tracking-widest text-ash/70 mt-6">Fabric — {product.fabric}</p>

              <div className="mt-10">
                <div className="flex items-center justify-between mb-4">
                  <p className="overline">Select Size</p>
                  <SizeGuide />
                </div>
                <div className="flex flex-wrap gap-3">
                  {product.sizes?.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      data-testid={`size-${s}`}
                      className={`min-w-[52px] px-4 py-3 border font-mono text-xs uppercase transition-colors duration-300 ${
                        size === s ? "bg-bone text-ink border-bone" : "border-line text-ash hover:border-bone hover:text-bone"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={add} data-testid="add-to-cart" className="btn-fill w-full py-5 mt-10 flex items-center justify-center gap-3 overline text-sm">
                Add to Bag <Plus size={16} />
              </button>
              <p className="font-mono text-[10px] text-ash mt-4 text-center uppercase tracking-widest">Free shipping across India · Made to order</p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
